"use client";

import { useAuth } from "@/hooks/use-auth";
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";

interface ModalSequenceContextValue {
	activeModal: "email" | "relationship" | null;
	isEmailModalActive: boolean;
	isRelationshipModalActive: boolean;
	completeEmailModal: () => void;
	completeRelationshipModal: () => void;
}

const ModalSequenceContext = createContext<ModalSequenceContextValue | null>(null);

export function ModalSequenceProvider({ children }: { children: ReactNode }) {
	const { isAuthenticated, user } = useAuth();
	const [activeModal, setActiveModal] = useState<"email" | "relationship" | null>(null);
	const [emailCompleted, setEmailCompleted] = useState(false);

	// Use refs to track which modals have been shown for the current session
	const hasShownEmailRef = useRef(false);
	const hasShownRelationshipRef = useRef(false);
	const currentUserIdRef = useRef<string | null>(null);

	useEffect(() => {
		if (!isAuthenticated || !user) {
			setActiveModal(null);
			// Reset refs when user logs out
			hasShownEmailRef.current = false;
			hasShownRelationshipRef.current = false;
			currentUserIdRef.current = null;
			return;
		}

		// Reset refs if user changed
		if (currentUserIdRef.current !== user.id) {
			hasShownEmailRef.current = false;
			hasShownRelationshipRef.current = false;
			currentUserIdRef.current = user.id;
		}

		// Check user's current state
		const hasEmails = user.emails && user.emails.length > 0;
		const hasGroups = user.groups && user.groups.length > 0;

		// Sequential logic based on requirements:
		// 1. If no emails and haven't shown email dialog -> show email dialog
		// 2. If has emails but no groups and haven't shown relationship dialog -> show relationship dialog
		// 3. If has both emails and groups -> show nothing

		if (!hasEmails && !hasShownEmailRef.current) {
			// No emails: show email dialog first
			setActiveModal("email");
		} else if (hasEmails && !hasGroups && !hasShownRelationshipRef.current) {
			// Has emails but no groups: show relationship dialog
			setActiveModal("relationship");
		}
		if (hasEmails && hasGroups) {
			// Has both emails and groups, or all necessary dialogs shown
			setActiveModal(null);
		}
	}, [isAuthenticated, user, emailCompleted]);

	const completeEmailModal = useCallback(() => {
		if (!user) return;
		const hasGroups = user.groups && user.groups.length > 0;

		hasShownEmailRef.current = true;
		// Trigger the useEffect to re-run by updating emailCompleted state
		setEmailCompleted(true);
		if (!hasGroups) setActiveModal("relationship");
		else setActiveModal(null);
	}, [user]);

	const completeRelationshipModal = useCallback(() => {
		hasShownRelationshipRef.current = true;
		setActiveModal(null);
	}, []);

	const contextValue: ModalSequenceContextValue = {
		activeModal,
		isEmailModalActive: activeModal === "email",
		isRelationshipModalActive: activeModal === "relationship",
		completeEmailModal,
		completeRelationshipModal,
	};

	return <ModalSequenceContext.Provider value={contextValue}>{children}</ModalSequenceContext.Provider>;
}

export function useModalSequence(): ModalSequenceContextValue {
	const context = useContext(ModalSequenceContext);
	if (!context) {
		throw new Error("useModalSequence must be used within ModalSequenceProvider");
	}
	return context;
}
